export const LEGAL_DOCS = {
  privacy: {
    title: "Privacy Policy",
    path: "/privacy-policy",
    sections: [
      {
        heading: "1. Introduction",
        paragraphs: [
          "VCK Business Lab (\"VCK\", \"we\", \"us\") is a virtual business support company based in Edmonton, Alberta, Canada, serving clients in Canada and internationally. This Privacy Policy explains how we collect, use, disclose, retain, and protect personal information in the course of operating our website and providing our services.",
          "This policy is structured around the principles of Alberta's Personal Information Protection Act (PIPA). Where you are located outside Alberta, additional mandatory privacy laws of your jurisdiction may also apply, and nothing in this policy is intended to exclude rights that cannot legally be waived.",
        ],
      },
      {
        heading: "2. Personal Information We May Collect",
        bullets: [
          "Contact information — name, email address, phone number, company name, country, and preferred contact method.",
          "Inquiry and quote information — the details you share in contact forms, quote requests, consultation bookings, and correspondence, including the type of support you are looking for.",
          "Client and project information — documents, instructions, project details, and other materials you provide so we can deliver services.",
          "Billing and payment information — invoice references, payment amounts, and transaction records. Card numbers are processed by our payment provider (Stripe) and are never stored on our servers.",
          "Website usage and technical information — such as pages visited, browser type, device, approximate location, and referring pages.",
        ],
      },
      {
        heading: "3. Cookies and Analytics",
        paragraphs: [
          "Our website uses a small number of cookies — for example, to remember your language preference — and may use privacy-conscious analytics to understand how the site is used. Please see our Cookie Policy for details and for how to control cookies through your browser settings.",
        ],
      },
      {
        heading: "4. Why We Collect Information",
        bullets: [
          "To respond to inquiries and prepare quotes.",
          "To schedule and deliver consultations and services.",
          "To manage client relationships, projects, invoicing, and payments.",
          "To send service-related communications, such as booking confirmations and appointment reminders.",
          "To improve our website, services, and client experience.",
          "To meet legal, accounting, and record-keeping obligations.",
        ],
      },
      {
        heading: "5. Consent",
        paragraphs: [
          "By submitting an inquiry, booking a consultation, or engaging our services, you consent to the collection, use, and disclosure of your personal information as described in this policy. You may withdraw consent at any time by contacting us, subject to legal and contractual restrictions; withdrawing consent may limit our ability to provide services to you.",
        ],
      },
      {
        heading: "6. Use and Disclosure",
        paragraphs: [
          "We use personal information only for the purposes described above or for purposes compatible with them. We do not sell personal information. We disclose personal information only as needed to deliver our services, as described in this policy, or as required or permitted by law.",
        ],
      },
      {
        heading: "7. Service Providers and Subcontractors",
        paragraphs: [
          "We may work with trusted service providers and subcontractors (for example, translators, administrative specialists, or technical assistants) who assist in delivering your project. They are given access only to the information needed for their work and are expected to protect it under confidentiality obligations.",
        ],
      },
      {
        heading: "8. Cloud and Software Providers",
        paragraphs: [
          "We use reputable cloud and software providers for email, scheduling, file storage, project management, payment processing, and website hosting. Your information may be processed by these third-party technology providers on our behalf, under their own security and privacy practices.",
        ],
      },
      {
        heading: "9. International Data Processing",
        paragraphs: [
          "Because we operate virtually and serve clients internationally, your information may be processed by third-party technology providers and may be stored outside your province or country, including in the United States or other jurisdictions, where permitted by law. Information stored in another jurisdiction may be subject to the laws of that jurisdiction.",
        ],
      },
      {
        heading: "10. Information Security",
        paragraphs: [
          "We use reasonable administrative, technical, and physical safeguards appropriate to the sensitivity of the information — including access controls, secure platforms, and confidentiality practices. However, no method of transmission or storage is completely secure, and we cannot guarantee absolute data security.",
        ],
      },
      {
        heading: "11. Confidential Client Information",
        paragraphs: [
          "Client materials, business information, and project details are treated as confidential, used only to deliver your services, and shared only with the people and providers involved in your engagement, or as required by law.",
        ],
      },
      {
        heading: "12. Retention and Secure Deletion",
        paragraphs: [
          "We keep personal information only as long as needed for the purposes it was collected for, and to meet legal, accounting, or contractual requirements. When no longer needed, information is securely deleted or destroyed.",
        ],
      },
      {
        heading: "13. Access and Correction Requests",
        paragraphs: [
          "You may request access to your personal information, or ask us to correct it, by contacting us at the address below. We will respond within a reasonable time and in accordance with applicable law.",
        ],
      },
      {
        heading: "14. Privacy Complaints",
        paragraphs: [
          "If you have a concern about how we handle personal information, please contact us first so we can try to resolve it. If you are not satisfied, you may contact the Office of the Information and Privacy Commissioner of Alberta.",
        ],
      },
      {
        heading: "15. Security Incidents and Data Breaches",
        paragraphs: [
          "If a security incident creates a real risk of significant harm, we will notify affected individuals and the appropriate regulator as required by applicable law, and we will take reasonable steps to reduce the risk of harm.",
        ],
      },
      {
        heading: "16. Privacy Officer",
        paragraphs: [
          "The Privacy Officer is responsible for privacy questions, access and correction requests, complaints, and privacy incident response. You can reach the Privacy Officer at:",
          "Privacy Officer, VCK Business Lab, Edmonton, Alberta, Canada — office@vckbusinesslab.com",
        ],
      },
      {
        heading: "17. Marketing Communications (CASL)",
        paragraphs: [
          "VCK Business Lab may send commercial electronic messages only where permitted by applicable law, including Canada's Anti-Spam Legislation (CASL). Where required, VCK obtains consent before sending promotional communications.",
          "Commercial messages from VCK will identify VCK Business Lab, include valid contact information, and provide a working unsubscribe mechanism. You may withdraw your consent to receive marketing communications at any time, and unsubscribe requests are processed promptly and within the period required by law.",
          "After an unsubscribe request, we may retain limited suppression information to help ensure you are not inadvertently added back to marketing lists.",
        ],
      },
      {
        heading: "18. Changes to This Policy",
        paragraphs: [
          "We may update this Privacy Policy from time to time. The current version will always be posted on this page with its \"Last Updated\" date.",
        ],
      },
      {
        heading: "19. Privacy Contact",
        paragraphs: [
          "Privacy questions, access or correction requests, and complaints can be sent to office@vckbusinesslab.com. VCK Business Lab is based in Edmonton, Alberta, Canada.",
        ],
      },
    ],
  },
  terms: {
    title: "Website Terms of Use",
    path: "/terms-of-use",
    sections: [
      {
        heading: "1. Acceptance of These Terms",
        paragraphs: [
          "By accessing or using the VCK Business Lab website, you accept these Website Terms of Use. If you do not agree, please do not use the website.",
        ],
      },
      {
        heading: "2. Informational Purpose",
        paragraphs: [
          "This website is provided for general informational purposes about VCK Business Lab and its services. Content on the website does not constitute an offer capable of acceptance, and service engagements are formed only through a written quote, service agreement, statement of work, or booking confirmation.",
        ],
      },
      {
        heading: "3. No Professional Advice",
        paragraphs: [
          "Nothing on this website constitutes professional advice of any kind — including legal, accounting, tax, immigration, medical, or engineering advice. VCK Business Lab does not provide professional engineering services, legal advice, accounting or tax advice, immigration advice, or medical advice, and website content should not be relied upon as such.",
        ],
      },
      {
        heading: "4. No Client Relationship",
        paragraphs: [
          "Using this website, submitting an inquiry, booking a consultation, or receiving a response does not create a client relationship. A client relationship begins only when a written quote, service agreement, statement of work, or booking confirmation is accepted by both parties.",
        ],
      },
      {
        heading: "5. Intellectual Property",
        paragraphs: [
          "The content of this website — including text, design, graphics, logos, and layout — is owned by or licensed to VCK Business Lab and is protected by applicable intellectual property laws. You may view and share links to this website, but you may not copy, reproduce, or republish its content for commercial purposes without written permission.",
        ],
      },
      {
        heading: "6. Permitted and Prohibited Use",
        paragraphs: [
          "You may use this website for lawful purposes only. You agree not to misuse the website, attempt to gain unauthorized access to any part of it or its systems, scrape or harvest information in bulk, submit false or misleading information, interfere with the website's operation, or use the website in any way that violates applicable law.",
        ],
      },
      {
        heading: "7. Accuracy of Information",
        paragraphs: [
          "We work to keep website information accurate and current, but we do not warrant that all content is complete, accurate, or up to date at all times. Service descriptions, availability, and pricing may change.",
        ],
      },
      {
        heading: "8. Pricing",
        paragraphs: [
          "Prices shown on this website are starting or indicative prices only, unless confirmed in a written quote or agreement for your specific project. Final pricing depends on scope, complexity, volume, urgency, and the terms agreed in writing.",
        ],
      },
      {
        heading: "9. Third-Party Links and Services",
        paragraphs: [
          "This website links to or integrates third-party services, such as scheduling (Cal.com), payment processing (Stripe), and messaging (WhatsApp). We do not control and are not responsible for third-party websites, services, their content, or their privacy practices.",
        ],
      },
      {
        heading: "10. Availability and Interruptions",
        paragraphs: [
          "We aim to keep the website available, but it may be interrupted or unavailable from time to time — for maintenance, updates, technical issues, or reasons outside our control — without notice.",
        ],
      },
      {
        heading: "11. Limitation of Liability",
        paragraphs: [
          "To the fullest extent permitted by applicable law, VCK Business Lab is not liable for any indirect, incidental, consequential, or special damages arising from the use of, or inability to use, this website or its content. Nothing in these terms excludes or limits liability that cannot legally be excluded or limited.",
        ],
      },
      {
        heading: "12. Indemnity",
        paragraphs: [
          "To the extent permitted by law, you agree to indemnify and hold VCK Business Lab harmless from claims, losses, or expenses arising from your misuse of this website or your breach of these terms.",
        ],
      },
      {
        heading: "13. Changes to the Website or These Terms",
        paragraphs: [
          "We may update the website and these terms from time to time. The current version will be posted on this page with its \"Last Updated\" date, and continued use of the website means you accept the updated terms.",
        ],
      },
      {
        heading: "14. Governing Law",
        paragraphs: [
          "These terms are governed by the laws of the Province of Alberta and the federal laws of Canada applicable there. If you access this website from another jurisdiction, mandatory local laws of that jurisdiction may also apply, and nothing in these terms is intended to override rights that cannot lawfully be waived.",
        ],
      },
    ],
  },
  service: {
    title: "Service Terms & General Disclaimer",
    path: "/service-terms",
    sections: [
      {
        heading: "1. How Engagements Begin",
        paragraphs: [
          "All VCK Business Lab services require acceptance of a quote, service agreement, statement of work, booking confirmation, or other written agreement before work begins. Nothing on this website — including service descriptions and listed prices — creates an engagement on its own.",
        ],
      },
      {
        heading: "2. Scope, Deadlines, and Fees",
        paragraphs: [
          "All services are subject to availability. Scope, deliverables, deadlines, revision rounds, and fees depend on the individual project and are defined in the written quote or agreement for that engagement. The terms of a signed agreement take precedence over general website information.",
        ],
      },
      {
        heading: "3. Client Information and Materials",
        paragraphs: [
          "The client is responsible for providing complete, accurate, and timely information, instructions, and materials. VCK may rely on information and materials supplied by the client and is not responsible for errors in the final work that result from inaccurate or incomplete client-supplied information.",
        ],
      },
      {
        heading: "4. Client Delays",
        paragraphs: [
          "Deadlines assume timely client communication and approvals. Client delays — such as late feedback, missing materials, or postponed approvals — may correspondingly affect deadlines and delivery dates.",
        ],
      },
      {
        heading: "5. No Guaranteed Outcomes",
        paragraphs: [
          "VCK Business Lab does not guarantee specific business results, including sales, revenue, profit, search engine rankings, website traffic, customer acquisition, regulatory acceptance of documents, or other outcomes. We commit to professional, diligent work — not to particular results, which depend on many factors outside our control.",
        ],
      },
      {
        heading: "6. Corrections",
        paragraphs: [
          "If VCK makes a material service error, please tell us promptly. Subject to applicable law, VCK should be given a reasonable opportunity to correct or re-perform the affected work before other remedies are sought.",
        ],
      },
      {
        heading: "7. Third-Party Services",
        paragraphs: [
          "Third-party software, hosting providers, domain registrars, plugins, payment platforms, scheduling tools, and similar services are outside VCK's direct control. We are not responsible for outages, changes, data loss, pricing changes, or discontinuation of third-party services, though we will make reasonable efforts to help you adapt.",
        ],
      },
      {
        heading: "8. Review and Approval",
        paragraphs: [
          "The client is responsible for reviewing and approving final content, documents, translations, and website materials before publication or use. Once approved or published by the client, responsibility for the use of the final material rests with the client.",
        ],
      },
      {
        heading: "9. Relationship to Signed Agreements",
        paragraphs: [
          "These Service Terms supplement — and do not replace — any Master Services Agreement, signed quote, service schedule, or Statement of Work between you and VCK Business Lab. If there is a conflict, the signed agreement governs.",
        ],
      },
    ],
  },
  translation: {
    title: "Translation & Interpretation Disclaimer",
    path: "/translation-disclaimer",
    sections: [
      {
        heading: "1. General and Business Language Services",
        paragraphs: [
          "VCK Business Lab's standard, publicly advertised language services are general and business translation and interpretation — for everyday business communication, meetings, documents, customer interactions, websites, presentations, and similar non-regulated purposes.",
        ],
      },
      {
        heading: "2. Your Responsibility to Explain the Intended Use",
        paragraphs: [
          "You are responsible for telling us the intended use of the translation or interpretation and any requirements of the receiving organization before work begins, so we can confirm whether the work is within our scope.",
        ],
      },
      {
        heading: "3. Not Certified Unless Expressly Stated",
        paragraphs: [
          "General translations should not automatically be treated as certified, sworn, legal, or officially accepted documents. VCK Business Lab does not represent its general translation or interpretation services as certified, sworn, court-certified, legal, or otherwise officially accredited unless this is expressly stated for a specific project and delivered by an appropriately qualified professional.",
        ],
      },
      {
        heading: "4. No Guarantee of Acceptance",
        paragraphs: [
          "We do not guarantee that any translation will be accepted by courts, government bodies, immigration authorities, educational institutions, banks, or other third parties, unless the specific qualification requirement was confirmed in advance and the work was arranged to meet it.",
        ],
      },
      {
        heading: "5. Interpretation Quality Factors",
        paragraphs: [
          "Interpretation quality can be affected by factors outside our control, including audio and connection quality, specialized terminology, incomplete background information, interruptions, and the speaking pace or clarity of participants. Providing materials, glossaries, and context in advance helps us deliver the best possible result.",
        ],
      },
      {
        heading: "6. Not a Substitute for Professional Advice",
        paragraphs: [
          "General business interpretation and translation do not replace legal, medical, or other regulated professional advice. Court, legal, medical, or other regulated or specialized interpretation is provided only where specifically agreed in advance and the provider meets the applicable requirements.",
        ],
      },
    ],
  },
  website: {
    title: "Website Services Disclaimer",
    path: "/website-disclaimer",
    sections: [
      {
        heading: "1. Content Ownership and Permissions",
        paragraphs: [
          "The client is responsible for owning or having permission to use all content supplied for a website project — including text, images, logos, videos, fonts, and trademarks. VCK may rely on the client's confirmation that supplied materials are properly licensed.",
        ],
      },
      {
        heading: "2. Domains and Hosting",
        paragraphs: [
          "Unless a project agreement states otherwise, domain names and hosting accounts are registered in the client's name and remain the client's responsibility, including renewals. We can coordinate setup and renewals as part of an agreed scope, but third-party registrar and hosting terms apply.",
        ],
      },
      {
        heading: "3. Third-Party Software, Plugins, and Apps",
        paragraphs: [
          "Websites often rely on third-party platforms, themes, plugins, and apps. These are outside VCK's direct control: they may change, be updated, be discontinued, have vulnerabilities, or become incompatible over time.",
        ],
      },
      {
        heading: "4. E-Commerce and Payment Processors",
        paragraphs: [
          "Online payments are processed by third-party providers (for example, Stripe). Those providers' terms, fees, security practices, and availability apply, and VCK is not responsible for their outages, decisions, or account actions.",
        ],
      },
      {
        heading: "5. Security Limitations",
        paragraphs: [
          "We follow reasonable security practices when building and maintaining websites, but no website can be completely secure, uninterrupted, or error-free. Security is a shared, ongoing responsibility that includes the client keeping accounts and credentials safe.",
        ],
      },
      {
        heading: "6. Backups",
        paragraphs: [
          "Backup arrangements depend on the platform and the agreed scope. Unless ongoing maintenance including backups is part of your agreement, the client is responsible for maintaining their own backups.",
        ],
      },
      {
        heading: "7. Compatibility Changes",
        paragraphs: [
          "Browsers, devices, and platforms evolve. A website that works today may need updates to remain compatible in the future; such updates are outside the original build scope unless covered by a maintenance plan.",
        ],
      },
      {
        heading: "8. Search Engine Rankings",
        paragraphs: [
          "We build websites with sound technical and content practices, but we do not guarantee search engine rankings, indexing speed, traffic levels, or placement for any keyword.",
        ],
      },
      {
        heading: "9. Accessibility and Legal Compliance",
        paragraphs: [
          "Where applicable, the client is responsible for telling us about specific accessibility, privacy, or regulatory requirements for their website (for example, accessibility legislation or industry rules) so they can be addressed in the agreed scope. Unless specifically included in the agreement, formal accessibility audits and legal compliance reviews are not part of a website build.",
        ],
      },
      {
        heading: "10. Client Approval Before Launch",
        paragraphs: [
          "Websites are launched or made live only after client review and approval. Once approved and launched, responsibility for published content rests with the client.",
        ],
      },
      {
        heading: "11. Ongoing Maintenance",
        paragraphs: [
          "Updates, monitoring, and maintenance after launch are provided only when included in a written agreement or monthly support plan.",
        ],
      },
    ],
  },
  refund: {
    title: "Payment, Cancellation & Refund Policy",
    path: "/refund-policy",
    sections: [
      {
        heading: "1. Payment Structure",
        bullets: [
          "Small projects may require full payment before work begins.",
          "Larger projects may require a deposit, with milestone and final payments as set out in the quote or agreement.",
          "Monthly support plans are billed in advance for each billing period.",
          "Payments are made through a secure payment link sent directly to you by VCK Business Lab once your service, scope, and pricing are confirmed — this website does not offer public checkout.",
          "Priority, rush, or emergency work may require advance payment.",
        ],
      },
      {
        heading: "2. Deposits",
        paragraphs: [
          "Deposits reserve time and resources for your project. To the extent permitted by law, a deposit may become non-refundable once work has begun.",
        ],
      },
      {
        heading: "3. Completed Work and Time",
        paragraphs: [
          "Completed work and time already performed are generally non-refundable, as they cannot be returned.",
        ],
      },
      {
        heading: "4. If We Make a Material Error",
        paragraphs: [
          "If VCK makes a material error in delivered work, please notify us promptly. Subject to applicable law, VCK should first receive a reasonable opportunity to correct or re-perform the affected work before refunds or other remedies are pursued.",
        ],
      },
      {
        heading: "5. Cancellations",
        paragraphs: [
          "Cancellation terms may differ by service and by your signed agreement. For monthly support plans, cancellation takes effect as set out in your agreement — typically at the end of the current prepaid billing period — and amounts already billed for the current period are not refunded unless required by law or stated in your agreement.",
        ],
      },
      {
        heading: "6. Third-Party Expenses",
        paragraphs: [
          "Third-party expenses and already-incurred costs on your behalf (for example, domains, software licences, stock assets, or subcontracted work already performed) are generally non-refundable.",
        ],
      },
      {
        heading: "7. Your Mandatory Rights",
        paragraphs: [
          "Nothing in this policy removes or limits any mandatory consumer rights you have under applicable law. Where those laws give you additional rights, they continue to apply.",
        ],
      },
    ],
  },
  cookies: {
    title: "Cookie Policy",
    path: "/cookie-policy",
    sections: [
      {
        heading: "1. What Cookies Are",
        paragraphs: [
          "Cookies are small text files stored on your device when you visit a website. They help websites function, remember preferences, and understand how the site is used.",
        ],
      },
      {
        heading: "2. Cookies We Use",
        bullets: [
          "Essential cookies — required for the website to function (for example, security and load-balancing cookies from our hosting platform).",
          "Functionality cookies — remember choices you make, such as your preferred website language.",
          "Analytics cookies — may be used to understand, in aggregate, how visitors use the site so we can improve it.",
          "Third-party integrations — some embedded or linked services (such as scheduling, payment, or messaging tools) may set their own cookies when you interact with them, under their own privacy policies.",
        ],
      },
      {
        heading: "3. How to Control Cookies",
        paragraphs: [
          "You can control or delete cookies through your browser settings at any time, and most browsers let you block cookies entirely or alert you before a cookie is stored. Where the site provides a cookie-preference tool, you can also manage non-essential cookies there. Blocking some cookies may affect how parts of the website function.",
        ],
      },
      {
        heading: "4. Changes to This Policy",
        paragraphs: [
          "We may update this Cookie Policy as the website evolves. The current version will always be posted on this page with its \"Last Updated\" date.",
        ],
      },
    ],
  },
  accessibility: {
    title: "Accessibility Statement",
    path: "/accessibility",
    sections: [
      {
        heading: "1. Our Commitment",
        paragraphs: [
          "VCK Business Lab aims to make its website usable and accessible to as many people as possible, including people using assistive technologies. We approach accessibility as an ongoing effort rather than a one-time project.",
        ],
      },
      {
        heading: "2. Feedback Welcome",
        paragraphs: [
          "If you experience difficulty using any part of this website, or have suggestions that would make it more accessible to you, please contact us at office@vckbusinesslab.com. We will review your feedback and attempt reasonable improvements.",
        ],
      },
      {
        heading: "3. Good Faith Effort",
        paragraphs: [
          "This statement reflects our good-faith commitment to accessibility. We do not claim formal WCAG certification or full compliance with any specific standard, and we appreciate the opportunity to improve.",
        ],
      },
    ],
  },
  newcomer: {
    title: "Newcomer Support Disclaimer",
    path: "/newcomer-disclaimer",
    sections: [
      {
        heading: "1. Scope of Newcomer Support Services",
        paragraphs: [
          "VCK Business Lab provides general administrative, language, translation, interpretation, coordination, and practical support services.",
        ],
      },
      {
        heading: "2. No Immigration or Citizenship Advice",
        paragraphs: [
          "VCK Business Lab does not provide immigration or citizenship advice or representation and is not acting as an immigration consultant or immigration lawyer.",
          "We do not advise clients on immigration eligibility, immigration programs, visa or permit strategies, permanent residence applications, citizenship applications, or how immigration applications should be completed.",
        ],
      },
      {
        heading: "3. No Other Regulated Professional Advice",
        paragraphs: [
          "We also do not provide legal, tax, financial, medical, educational, employment, or other regulated professional advice.",
        ],
      },
      {
        heading: "4. Referrals to Licensed Professionals",
        paragraphs: [
          "Where a client requires regulated professional services, VCK Business Lab may recommend that the client contact an appropriately licensed professional.",
          "Administrative and language assistance provided by VCK Business Lab should not be interpreted as professional advice or representation.",
        ],
      },
      {
        heading: "5. Contact",
        paragraphs: [
          "VCK Business Lab — Edmonton, Alberta, Canada",
          "Email: office@vckbusinesslab.com • Phone: +1 587 385 6058 • Web: vckbusinesslab.com",
        ],
      },
    ],
  },
};

export const LEGAL_LINKS = [
  { key: "privacy", label: "Privacy Policy" },
  { key: "terms", label: "Terms of Use" },
  { key: "service", label: "Service Terms" },
  { key: "translation", label: "Translation Disclaimer" },
  { key: "website", label: "Website Disclaimer" },
  { key: "refund", label: "Refund & Cancellation Policy" },
  { key: "cookies", label: "Cookie Policy" },
  { key: "accessibility", label: "Accessibility" },
  { key: "newcomer", label: "Newcomer Support Disclaimer" },
];

import { LEGAL_DOCS_UK, LEGAL_LINKS_UK } from "./legal-uk";
import { LEGAL_DOCS_RU, LEGAL_LINKS_RU } from "./legal-ru";
import { LEGAL_DOCS_AR, LEGAL_LINKS_AR } from "./legal-ar";

const LEGAL_BY_LANG = {
  uk: { docs: LEGAL_DOCS_UK, links: LEGAL_LINKS_UK },
  ru: { docs: LEGAL_DOCS_RU, links: LEGAL_LINKS_RU },
  ar: { docs: LEGAL_DOCS_AR, links: LEGAL_LINKS_AR },
};

export const getLegal = (lang) =>
  LEGAL_BY_LANG[lang] || { docs: LEGAL_DOCS, links: LEGAL_LINKS };
